import { useContactModal } from '@contexts/ContactModalContext';

/**
 * ContactHelpBanner — reusable contextual help banner
 *
 * Drop this into any page to provide contextual "need help?" contact triggers.
 * Accepts an array of topic buttons to display.
 *
 * Props:
 *   topics   - array of { topicId, label, icon } to show as buttons
 *   context  - context object to pass to the contact modal (product, sku, order, etc.)
 *   title    - optional banner title (default: "Need help?")
 *   compact  - if true, renders inline links instead of a bordered banner
 */
export default function ContactHelpBanner({ topics = [], context = {}, title, compact = false }) {
  const { openContact } = useContactModal();

  if (topics.length === 0) return null;

  if (compact) {
    return (
      <div className="flex flex-wrap items-center gap-3 py-2">
        {title && <span className="text-xs text-n5 font-sandvik-text">{title}</span>}
        {topics.map(t => (
          <button key={t.topicId} onClick={() => openContact(t.topicId, context)}
            className="text-electric-blue hover:underline font-sandvik-text text-sm flex items-center gap-1">
            {t.icon && <span className="material-symbols-outlined text-sm">{t.icon}</span>}
            {t.label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="border border-n2 p-4 bg-n1/50 mt-6">
      <div className="flex items-start gap-3">
        <span className="material-symbols-outlined text-xl text-electric-blue flex-shrink-0 mt-0.5">help</span>
        <div>
          <p className="text-sm font-medium text-carbon-black font-sandvik-text mb-2">
            {title || 'Need help?'}
          </p>
          <div className="flex flex-wrap gap-3">
            {topics.map(t => (
              <button key={t.topicId} onClick={() => openContact(t.topicId, context)}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-n3 rounded text-xs font-sandvik-text text-carbon-black hover:border-electric-blue hover:text-electric-blue transition-colors bg-white">
                {t.icon && <span className="material-symbols-outlined text-sm">{t.icon}</span>}
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
